import {
  type BotErrorEvent,
  type BotTier,
  DEFAULT_SETTINGS,
  type CharacterId,
  GamePhase,
  type HpRisk,
  type MapId,
  type MatchSettings,
  type RacialDef,
  type RoundResultEvent,
  type RuneId,
  type SlotKey,
  type SpellId,
} from '@arena/shared';
import { create } from 'zustand';

/** What the HUD shows about one player. Positions never go through React. */
export interface RosterEntry {
  sessionId: string;
  name: string;
  /** The seat's bot tier, or `''` for a human. */
  botTier: BotTier | '';
  /** Always a catalog id: unknown ids from the server resolve to the default. */
  character: CharacterId;
  colorIndex: number;
  team: number;
  ready: boolean;
  /** Voted to end the shop early (reset on every Shop entry). */
  shopReady: boolean;
  connected: boolean;
  alive: boolean;
  /** Whole-percent HP, so small HP ticks don't re-render the HUD. */
  hpPct: number;
  gold: number;
  points: number;
  kills: number;
  deaths: number;
  /** Damage dealt to other warlocks this match. */
  damageDealt: number;
  /** Active runes, in slot order (runes are public: the Tab scoreboard shows them). */
  runes: RuneId[];
}

export interface SpellSlotView {
  spellId: string;
  level: number;
  cooldownEndsAt: number;
  /** The active RuneId; absent or '' = the base spell. */
  rune?: string;
  /** RuneIds unlocked for this spell, in unlock order; absent = none. */
  unlocked?: readonly string[];
}

/** A player's racial for the HUD slot; null when racials are off or the character has none. */
export interface RacialView {
  def: RacialDef;
  /** Server clock ms when a Space racial is ready again; 0 = ready. */
  cooldownEndsAt: number;
  /** Server clock ms until which Stone Guard is up; 0 = down. */
  guardUntil: number;
  /** Server clock ms until which Stealth is up; 0 = down. */
  stealthUntil: number;
  /** A passive's condition holds right now (Fleetfoot on stone). */
  active: boolean;
}

/** The local player's own panel: HP, knockback readout and status effects. */
export interface SelfVitals {
  alive: boolean;
  /** Whole HP, rounded up, so small lava ticks don't re-render the HUD every patch. */
  hp: number;
  maxHp: number;
  /** Knockback multiplier taken right now (`knockbackReadout`), resistance included. */
  kb: number;
  risk: HpRisk;
  /** Knocked back above walking control. */
  sliding: boolean;
  /** Standing on lava during a fight. */
  onLava: boolean;
  /** Lava damage per second while `onLava` (0 while Emberward is up). */
  lavaDps: number;
  /** Server clock ms; 0 = no shield. */
  shieldUntil: number;
  /** Server clock ms when a Link tether breaks; 0 = not tethered. */
  tetherEndsAt: number;
  /** Server clock ms until which a Kindling burn ticks; 0 = none. */
  burnUntil: number;
  /** Server clock ms until which Emberward is up; 0 = none. */
  wardUntil: number;
  /** Server clock ms until which your Stealth is up; 0 = none. */
  stealthUntil: number;
}

export const NO_VITALS: SelfVitals = {
  alive: false,
  hp: 0,
  maxHp: 0,
  kb: 1,
  risk: 'healthy',
  sliding: false,
  onLava: false,
  lavaDps: 0,
  shieldUntil: 0,
  tetherEndsAt: 0,
  burnUntil: 0,
  wardUntil: 0,
  stealthUntil: 0,
};

/** The player a dead local player is spectating, with their spells for the read-only bar. */
export interface WatchedView {
  sessionId: string;
  /** Their character, for racial cooldown changes on the sweep. */
  character: CharacterId;
  spells: Partial<Record<SlotKey, SpellSlotView>>;
  racial: RacialView | null;
}

export interface KillFeedEntry {
  id: number;
  victimName: string;
  victimCharacter: CharacterId;
  victimBot: boolean;
  /** Name colour in Teams mode (`tintFor` when the kill happened); null in FFA or if unknown. */
  victimTint: string | null;
  killerName: string | null;
  killerCharacter: CharacterId | null;
  killerBot: boolean;
  killerTint: string | null;
  /** Top other damage dealer in the kill-credit window, if any. */
  assistName: string | null;
  assistBot: boolean;
  assistTint: string | null;
  /** The killer's last spell on the victim (icon between the names), if known. */
  spellId: SpellId | null;
  /** Burned to death on the lava (lava glyph). */
  lava: boolean;
  /** The local player is the killer or the victim (highlighted row). */
  mine: boolean;
  /** performance.now() when received. */
  at: number;
}

/** The part of the match snapshot derived from synced state (see net/bridge.ts). */
export interface MatchSnapshot {
  phase: GamePhase;
  phaseEndsAt: number;
  /** Room clock ms of the next arena shrink; 0 = none coming. */
  nextShrinkAt: number;
  round: number;
  /** Teams mode: rounds won per team, indexed by `team` (`[0, blue, red]`); empty before a match. */
  teamRoundsWon: number[];
  hostId: string;
  roster: RosterEntry[];
  /** The local player's HP, knockback and status effects. */
  self: SelfVitals;
  /** Who a dead local player is watching during a fight (see `watchedId`), or null. */
  watched: WatchedView | null;
  /** The local player's spell slots, keyed by slot. */
  spells: Partial<Record<SlotKey, SpellSlotView>>;
  /** The local player's racial slot, or null (hidden). */
  racial: RacialView | null;
  settings: MatchSettings;
  /** The map in play (`state.map.mapId`); in the shop it is the upcoming fight's map. */
  mapId: MapId;
}

export const KILL_FEED_SIZE = 5;

interface MatchState extends MatchSnapshot {
  selfId: string;
  /** Slot waiting for a target click (normal cast mode), or null. */
  armedSlot: SlotKey | null;
  /** Last cast tried on cooldown or on an empty slot; `id` changes per try so repeats re-flash. */
  notReady: { slot: SlotKey | 'racial'; id: number } | null;
  /**
   * The spectating pick (←/→, or whoever killed us). Only a preference: `watchedId` falls back
   * to another living player when it is null or dead.
   */
  spectateId: string | null;
  killFeed: KillFeedEntry[];
  lastRoundResult: RoundResultEvent | null;
  /** Latest `botError` for the toast; `id` changes per message so repeats re-show it. */
  botError: { id: number; reason: BotErrorEvent['reason'] } | null;
  /** The server said the bot service is off; the host sees a Retry instead of the bot controls. */
  botsUnavailable: boolean;
}

export const initialMatchState = (): MatchState => ({
  selfId: '',
  phase: GamePhase.Waiting,
  phaseEndsAt: 0,
  nextShrinkAt: 0,
  round: 0,
  teamRoundsWon: [],
  hostId: '',
  roster: [],
  self: NO_VITALS,
  watched: null,
  spells: {},
  racial: null,
  settings: { ...DEFAULT_SETTINGS },
  mapId: DEFAULT_SETTINGS.startMapId,
  armedSlot: null,
  notReady: null,
  spectateId: null,
  killFeed: [],
  lastRoundResult: null,
  botError: null,
  botsUnavailable: false,
});

export const useMatchStore = create<MatchState>(initialMatchState);

let nextKillId = 1;

export function pushKill(kill: Omit<KillFeedEntry, 'id'>): void {
  useMatchStore.setState((s) => ({
    killFeed: [...s.killFeed, { id: nextKillId++, ...kill }].slice(-KILL_FEED_SIZE),
  }));
}

let nextBotErrorId = 1;

export function showBotError(reason: BotErrorEvent['reason']): void {
  useMatchStore.setState((s) => ({
    botError: { id: nextBotErrorId++, reason },
    botsUnavailable: s.botsUnavailable || reason === 'unavailable',
  }));
}

/** Show the host's bot controls again, e.g. after the bot service was started. */
export function retryBots(): void {
  useMatchStore.setState({ botsUnavailable: false });
}

/** Hide the toast, unless a newer error replaced it meanwhile. */
export function clearBotError(id: number): void {
  if (useMatchStore.getState().botError?.id === id) useMatchStore.setState({ botError: null });
}

export function resetMatch(selfId = ''): void {
  useMatchStore.setState({ ...initialMatchState(), selfId });
}

/** Players a dead local player can watch: everyone else still alive, in join order. */
export function spectateCandidates(roster: readonly RosterEntry[], selfId: string): RosterEntry[] {
  return roster.filter((p) => p.alive && p.sessionId !== selfId);
}

/**
 * Who the camera and read-only spell bar follow: null unless the local player is dead in a fight.
 * The pick if it is still alive, otherwise the first living player.
 */
export function watchedId(
  phase: GamePhase,
  roster: readonly RosterEntry[],
  selfId: string,
  pick: string | null,
): string | null {
  if (phase !== GamePhase.Fight) return null;
  const self = roster.find((p) => p.sessionId === selfId);
  if (!self || self.alive) return null;
  const alive = spectateCandidates(roster, selfId);
  return alive.find((p) => p.sessionId === pick)?.sessionId ?? alive[0]?.sessionId ?? null;
}

/** ←/→ while spectating: the next (`dir` 1) or previous (-1) living player, wrapping around. */
export function cycleSpectate(dir: 1 | -1): void {
  const { phase, roster, selfId, spectateId } = useMatchStore.getState();
  const alive = spectateCandidates(roster, selfId);
  if (alive.length === 0) return;
  const current = watchedId(phase, roster, selfId, spectateId);
  const i = alive.findIndex((p) => p.sessionId === current);
  const next = alive[(i + dir + alive.length) % alive.length]!;
  useMatchStore.setState({ spectateId: next.sessionId });
}
